/**
 * ModuleConnections — the "this connects to…" line that makes the cross-module
 * moat visible (CLARID-UX-2026-06, PR 2).
 *
 * Reads a module's `needs` (upstream inputs) and `feeds` (downstream consumers)
 * from the shared help graph and renders them as links. Surfaced on packaging /
 * roll-up pages (Board Reports, Risk Assessments, Gap Analysis, Exam Readiness)
 * so a new user sees that the page draws from — and feeds — the rest of Clarid,
 * not a single silo. Renders nothing when a module has no connections.
 */

import { Link } from 'react-router-dom';
import { ArrowDownLeftIcon, ArrowUpRightIcon } from '@heroicons/react/20/solid';
import clsx from 'clsx';
import { helpContent } from '@/lib/help';
import type { ModuleKey } from '@/lib/help';
import { moduleRoutes } from '@/lib/help/moduleRoutes';

interface ModuleConnectionsProps {
  moduleKey: ModuleKey;
  className?: string;
}

function ConnectionRow({
  label,
  icon,
  keys,
}: {
  label: string;
  icon: React.ReactNode;
  keys: ModuleKey[];
}) {
  if (keys.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
      <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-500">
        {icon}
        {label}
      </span>
      {keys.map((key) => {
        const route = moduleRoutes[key];
        return (
          <Link
            key={key}
            to={route.path}
            className="inline-flex items-center rounded-full bg-white px-2 py-0.5 text-xs font-medium text-teal-700 ring-1 ring-inset ring-teal-200 transition-colors hover:bg-teal-50 hover:text-teal-800"
          >
            {route.label}
          </Link>
        );
      })}
    </div>
  );
}

export default function ModuleConnections({ moduleKey, className }: ModuleConnectionsProps) {
  const entry = helpContent[moduleKey];
  if (!entry) return null;

  const { needs, feeds } = entry;
  if (needs.length === 0 && feeds.length === 0) return null;

  return (
    <div
      className={clsx(
        'rounded-lg border border-gray-200 bg-gray-50/70 px-4 py-3',
        className,
      )}
    >
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
        Connected modules
      </p>
      <div className="space-y-1.5">
        <ConnectionRow
          label="Draws from"
          icon={<ArrowDownLeftIcon className="h-3.5 w-3.5 text-gray-400" />}
          keys={needs}
        />
        <ConnectionRow
          label="Feeds into"
          icon={<ArrowUpRightIcon className="h-3.5 w-3.5 text-gray-400" />}
          keys={feeds}
        />
      </div>
    </div>
  );
}
